import { ElementType, InputHTMLAttributes } from 'react'

import Typography from '@/components/base/typography'

import DatePicker from './date-picker'
import { cn } from '@/lib/utils'
import { WPCF7FormField } from '@/types'

type FormControlProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'onBlur'
> &
  Partial<
    WPCF7FormField & {
      error?: boolean
      options?: { value: string; text: string }[]
      // eslint-disable-next-line no-unused-vars
      onChange?: (e: any) => void
      // eslint-disable-next-line no-unused-vars
      onBlur?: (e: any) => void
      theme?: 'dark' | 'light'
    }
  >

const FormControl = ({
  id,
  type,
  label,
  checked,
  // eslint-disable-next-line no-unused-vars
  options,
  placeholder,
  className,
  children,
  theme = 'light',
  error,
  ...props
}: FormControlProps) => {
  const Component = (type === 'textarea' ? 'textarea' : 'input') as ElementType

  const renderInput = () => {
    switch (type) {
      case 'consent':
        return (
          <label
            // htmlFor={id}
            className="relative flex items-start space-x-[1rem]"
          >
            <input
              id={id}
              type="checkbox"
              checked={checked}
              className={cn(
                'h-[1.5rem] w-[1.5rem] opacity-0',
                '[&:checked+div]:after:bg-white'
              )}
              {...props}
            />
            <div
              className={cn(
                '',
                'absolute left-0 top-[.4rem] !m-0 h-[1.5rem] w-[1.5rem] shrink-0 cursor-pointer rounded-full border',
                'after:absolute after:left-1/2 after:top-1/2 after:size-[.8rem] after:-translate-x-1/2 after:-translate-y-1/2 after:rounded-full',
                {
                  'border-white': theme === 'light',
                  'border-red': error
                }
              )}
            />
            <Typography
              // variant="small"
              className={cn(
                'text-left font-light',
                '[&>#required-data]:mt-[1rem] [&>#required-data]:inline-block',
                {
                  'text-dark': theme === 'light',
                  '!text-error': error
                }
              )}
            >
              {label}
            </Typography>
          </label>
        )
      case 'date':
        return <DatePicker onChange={props.onChange} label={label} />
      default:
        return (
          <Component
            id={id}
            type={type}
            placeholder={placeholder || label}
            rows={type === 'textarea' ? 4 : undefined}
            className={cn(
              'flex h-[4rem] w-full rounded-[.5rem] bg-transparent px-[1.5rem]',
              type === 'textarea' && 'h-[8rem] py-[.7rem]',
              'appearance-none border outline-none',
              'focus:ring-2',
              {
                'border-havana text-dark placeholder:text-dark':
                  theme === 'light'
              },
              {
                'focus:havana focus:ring-havana/30':
                  !error && theme === 'light',
                'border-error focus:ring-error/20': error
              }
            )}
            {...props}
          />
        )
    }
  }

  return (
    <div
      className={cn('relative', className, {
        hidden: type === 'hidden'
      })}
    >
      {renderInput()}

      {children}
    </div>
  )
}

export default FormControl
